import { z } from "zod";

import { enterpriseOrgSeatsCreditsConfigurationSchema } from "../org/schemas";
import { OrgSubscriptionDetailsSchema, zCaptionsSubscriptionPlan } from "../skuba/schemas";

// Shared schemas
export const revenueCatBillingSubscriptionSchema = z.object({
  status: z.enum(["active", "cancelled", "expired"]),
  store: z.string(),
  productId: z.string(),
  displayName: z.string().nullish(),
  purchaseDate: z.string().nullish(),
  expiresDate: z.string().nullish(),
  unsubscribeDetectedAt: z.string().nullish(),
  isSandbox: z.boolean().nullish(),
  priceCents: z.number().nullish(),
  currency: z.string().nullish(),
  stripeCustomerId: z.string().nullish(),
  stripeKeyMode: z.enum(["live", "test"]).nullish(),
});
export type RevenueCatBillingSubscription = z.infer<typeof revenueCatBillingSubscriptionSchema>;

export const lookupBillingResponseSchema = z.object({
  revenueCat: z.object({
    appUserId: z.string(),
    subscription: revenueCatBillingSubscriptionSchema.nullable(),
  }),
});
export type LookupBilling = z.infer<typeof lookupBillingResponseSchema>;

export const internalAccountTypeSchema = z.object({
  type: z.enum(["externalTester", "internalAutomatedTester", "employee"]),
  removedAt: z.string().nullish(),
});

export type InternalAccountType = z.infer<typeof internalAccountTypeSchema>;

const userSchema = z.object({
  userId: z.string(),
  name: z.string(),
  email: z.string().nullish(),
  createdAt: z.string().nullish(),
  metadata: z.object({}).passthrough(),
  lastLoginAt: z.string().nullish(),
  lastLogin: z.string().nullish(),
  hasWorkosAccount: z.boolean().nullish(),
  phoneNumber: z.string().nullish(),
  deletedAt: z.string().nullish(),
  deletedReason: z.string().nullish(),
  internalAccountType: internalAccountTypeSchema.nullish(),
});

const workosOrgSchema = z.object({
  object: z.literal("organization"),
  id: z.string(),
  name: z.string(),
  allowProfilesOutsideOrganization: z.boolean(),
  domains: z.array(z.unknown()),
  createdAt: z.string(),
  updatedAt: z.string(),
  externalId: z.string().nullable(),
  metadata: z.record(z.unknown()),
});

const workosUserSchema = z.object({
  object: z.literal("user"),
  id: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  firstName: z.string().nullable(),
  profilePictureUrl: z.string().nullable(),
  lastName: z.string().nullable(),
  lastSignInAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  externalId: z.string().nullable(),
  metadata: z.record(z.unknown()),
});

const EnterpriseSubscriptionModel = z.object({
  subscriptionKind: z.object({
    plan: zCaptionsSubscriptionPlan,
    period: z.enum(["month", "year", "week"]),
  }),
  purchaseDates: z.object({
    purchase: z.string(),
    expiry: z.string(),
  }),
  store: z
    .enum(["app_store", "play_store", "mac_app_store", "amazon", "stripe", "promotional"])
    .optional(),
});

export type EnterpriseSubscription = z.infer<typeof EnterpriseSubscriptionModel>;

const orgDetailsSchema = z.object({
  id: z.string(),
  name: z.string(),
  skuba: OrgSubscriptionDetailsSchema.nullable(),
  enterpriseSeatsCredits: enterpriseOrgSeatsCreditsConfigurationSchema.nullish(),
  lastEnterpriseSubscription: EnterpriseSubscriptionModel.nullish(),
  stripeCustomerId: z.string().nullish(),
  stripeKeyMode: z.enum(["live", "test"]).nullish(),
});

const orgMemberSchema = z.discriminatedUnion("state", [
  z.object({
    state: z.literal("pending"),
    captionsUser: userSchema.nullable(),
    workosUser: workosUserSchema,
    role: z.object({
      slug: z.string(),
    }),
  }),
  z.object({
    state: z.literal("inactive"),
    captionsUser: userSchema.nullable(),
    workosUser: workosUserSchema,
    role: z.object({
      slug: z.string(),
    }),
  }),
  z.object({
    state: z.literal("active"),
    captionsUser: userSchema.nullable(),
    workosUser: workosUserSchema,
    role: z.object({
      slug: z.string(),
    }),
  }),
]);

export type OrgMember = z.infer<typeof orgMemberSchema>;

export const creditsBalanceSchema = z.object({
  credits: z.object({
    account: z.number(),
    limit: z.number(),
  }),
  renewsCreditsAt: z.string().nullable(),
});

const orgWithDetailsSchema = z.object({
  workosOrg: workosOrgSchema,
  orgDetails: orgDetailsSchema,
  orgMembers: z.array(orgMemberSchema),
  orgBalance: creditsBalanceSchema,
  environment: z.enum(["prod", "staging", "oasis_staging"]).nullish(),
});

const userLookupSchema = userSchema.extend({
  balance: creditsBalanceSchema.nullable(),
});

// Response Schema
export const lookupUserResponseSchema = z.object({
  user: userLookupSchema,
  orgs: z.array(orgWithDetailsSchema),
});

export const lookupOrgResponseSchema = z.object({
  org: orgWithDetailsSchema,
});

// User search schemas
const userSearchItemSchema = z.object({
  id: z.string(),
  name: z.string().nullish(),
  email: z.string().nullish(),
});

export const userSearchResponseSchema = z.object({
  users: z.array(userSearchItemSchema),
});

const internalTestAccountSchema = z.object({
  accountKey: z.string(),
  userId: z.string().nullish(),
  workosUserId: z.string().nullish(),
  email: z.string().nullish(),
  name: z.string().nullish(),
  displayName: z.string().nullish(),
  product: z.string().nullish(),
  orgIds: z.array(z.string()).nullish(),
  primaryOrgId: z.string().nullish(),
  orgRole: z.string().nullish(),
  status: z.string().nullish(),
  purpose: z.string().nullish(),
  owner: z.string().nullish(),
  notes: z.string().nullish(),
  createdAt: z.string().nullish(),
  updatedAt: z.string().nullish(),
  lastLoginAt: z.string().nullish(),
  lastSignInAt: z.string().nullish(),
  lastAuthMintedAt: z.string().nullish(),
  lastSeenAt: z.string().nullish(),
  lastTestRunId: z.string().nullish(),
  deletedAt: z.string().nullish(),
  disabledAt: z.string().nullish(),
  internalAccountType: internalAccountTypeSchema.nullish(),
  metadata: z.object({}).passthrough().nullish(),
  workosMetadata: z.object({}).passthrough().nullish(),
});

export const internalTestAccountsResponseSchema = z.object({
  accounts: z.array(internalTestAccountSchema),
});

const internalAccountListItemSchema = z.object({
  userId: z.string(),
  email: z.string().nullish(),
  name: z.string().nullish(),
  internalAccountType: internalAccountTypeSchema.nullish(),
});

export type InternalAccountListItem = z.infer<typeof internalAccountListItemSchema>;

export const internalAccountsResponseSchema = z.object({
  accounts: z.array(internalAccountListItemSchema),
});
